import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateStepContent } from '@partful/entities/instructions/stepContent';
import { StepContentModel } from '@partful/instructions-domain-models';
import { classNames } from '@partful/shared/lib/classNames';
import { Button } from '@partful/shared/ui/Button';
import { Input } from '@partful/shared/ui/Input';
import { ReactHookFormField } from '@partful/shared/ui/ReactHookFormField/ReactHookFormField';
import { Textarea } from '@partful/shared/ui/Textarea';
import { ComponentProps } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './StepContentForm.module.scss';

type StepContentFormProps = {
  content?: StepContentModel;
  onCancel?: () => void;
  onSubmit: SubmitHandler<UpdateStepContent>;
  disabled?: boolean;
} & Omit<ComponentProps<'form'>, 'onSubmit' | 'content'>;

export function StepContentForm({ content, onSubmit, disabled, className, ...rest }: Readonly<StepContentFormProps>) {
  const { t: i18n } = useTranslation();

  const { formState, handleSubmit, control } = useForm<UpdateStepContent>({
    resolver: zodResolver(UpdateStepContent),
    mode: 'all',
    values: {
      name: content?.name ?? '',
      description: content?.description ?? '',
      tools: content?.tools ?? '',
    },
  });

  return (
    <form {...rest} className={classNames(styles.form, className)} onSubmit={handleSubmit(onSubmit)}>
      <ReactHookFormField
        name="name"
        label={i18n('instructions:step.data.name.label')}
        id={`step-content-${content?.id ?? 'new'}-name`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            size="small"
            id={`step-content-${content?.id ?? 'new'}-name`}
            placeholder={i18n('instructions:step.data.name.placeholder')}
            disabled={disabled}
            error={error != null}
          />
        )}
      />
      <ReactHookFormField
        name="description"
        label={i18n('instructions:step.data.description.label')}
        id={`step-content-${content?.id ?? 'new'}-description`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Textarea
            {...field}
            size="small"
            id={`step-content-${content?.id ?? 'new'}-description`}
            placeholder={i18n('instructions:step.data.description.placeholder')}
            minRows={3}
            maxRows={10}
            disabled={disabled}
            error={error != null}
          />
        )}
      />
      <ReactHookFormField
        name="tools"
        label={i18n('instructions:step.data.tools.label')}
        id={`step-content-${content?.id ?? 'new'}-tools`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Textarea
            {...field}
            size="small"
            id={`step-content-${content?.id ?? 'new'}-tools`}
            placeholder={i18n('instructions:step.data.tools.placeholder')}
            minRows={3}
            maxRows={10}
            disabled={disabled}
            error={error != null}
          />
        )}
      />
      <Button
        name="instructions_stepeditor_save_click"
        type="submit"
        variant="primary"
        disabled={disabled || !formState.isDirty || !formState.isValid}
      >
        {i18n('instructions:common.save')}
      </Button>
    </form>
  );
}
